import { useState, useEffect } from 'react'
import s from './SalesAndForecast.scss'
import {
  makeReportsPostRequest,
  makeGetRequest,
  getReportsBaseUrl,
  makeReportsGetRequest
} from 'utils/api'
import { toUsers, toData, getColors, toYears } from './serializers'
import { useLoading } from 'hooks'
import { Col, Row, Table, UncontrolledTooltip } from 'reactstrap'
import Select from 'react-select'
import { FaInfoCircle, FaFileExcel } from 'react-icons/fa'
import { ResponsiveBar } from '@nivo/bar'
import { ResponsivePie } from '@nivo/pie'
import { getCurrency, getToken } from 'utils/authService'
import { NumericFormat } from 'react-number-format'
import { AddButton } from 'components/common/AddButton'

const SalesAndForecast = () => {
  const [result, setResult] = useState({})
  const [users, setUsers] = useState([])
  const [years, setYears] = useState([])
  const [prevYears, setPrevYears] = useState(years)
  const [userFilter, setUserFilter] = useState([])
  const [yearFilter, setYearFilter] = useState(null)
  const [, withLoadingLocks] = useLoading()

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))

      const years = await makeReportsGetRequest('/v1/sales/ytd/available_years')
      setYears(toYears(years))
    })()
  }, [])

  if (prevYears !== years) {
    setPrevYears(years)
    setYearFilter(years[years.length - 1])
  }

  useEffect(() => {
    if (!yearFilter) return
    ;(async () => {
      const result = await withLoadingLocks(
        makeReportsPostRequest('/v1/sales/ytd', {
          ytd: yearFilter?.value ?? new Date().getFullYear(),
          ...(userFilter && userFilter.length
            ? { userFilter: userFilter.map(user => user.value) }
            : {})
        })
      )
      setResult(toData(result))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter, yearFilter])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/sales/ytd/export?&ytd=${yearFilter.value}&userFilter=${userFilter
          .map(user => user.value)
          .join()}&token=${getToken()}`
      ),
      '_blank'
    )
  }

  return (
    <>
      <h6 className={s.title}>Sales Reports &gt; YTD + Annual Sales + Forecast Report</h6>
      <div className={s.filters}>
        <Select
          onChange={values => setYearFilter(values)}
          options={years}
          value={yearFilter}
          placeholder="Year to Date (YTD)"
          className={s.userSelect}
        />
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
      </div>
      {result.sales?.filter(s => s.salesPerson !== 'GRAND TOTAL').length ? (
        <>
          <AddButton onClick={onBtnExport}>
            <FaFileExcel className={s.excelIcon} />
            Export to Excel
          </AddButton>
          <Table striped style={{ fontSize: 14, marginBottom: 40 }}>
            <thead className={s.tableHead}>
              <tr>
                <th>Sales Person</th>
                <th>Target Revenue</th>
                <th>SOLD YTD</th>
                <th>% to Target Revenue</th>
                <th>Target Profit</th>
                <th>YTD Profit</th>
                <th>% to Target Profit</th>
                <th>Remaining to Quota</th>
                <th>Forecast</th>
                <th>SOLD + FORECAST</th>
                <th>Remaining to Quota with Forecast</th>
              </tr>
            </thead>
            <tbody>
              {result?.sales
                .filter(s => s.salesPerson !== 'GRAND TOTAL')
                .map(s => (
                  <tr key={s.salesPerson}>
                    <td>{s.salesPerson}</td>
                    <td>{s.targetRevenue}</td>
                    <td>{s.soldYTD}</td>
                    <td>{s.percentToTargetRevenue}</td>
                    <td>{s.targetProfit}</td>
                    <td>{s.profitYTD}</td>
                    <td>{s.percentToTargetProfit}</td>
                    <td>{s.remainingToQuota}</td>
                    <td>{s.forecast}</td>
                    <td>{s.soldAndForecast}</td>
                    <td>{s.remainingToQuotaWithForecast}</td>
                  </tr>
                ))}
              <tr className={s.tableTotal}>
                <td>GRAND TOTAL</td>
                <td>{result.totalTargetRevenue}</td>
                <td>{result.totalSoldYtd}</td>
                <td></td>
                <td>{result.totalTargetProfit}</td>
                <td>{result.totalProfitYTD}</td>
                <td></td>
                <td>{result.totalRemainingToQuota}</td>
                <td>{result.totalForecast}</td>
                <td>{result.totalSoldAndForecast}</td>
                <td>{result.totalRemainingToQuotaWithForecast}</td>
              </tr>
            </tbody>
          </Table>
          <div className={s.boxes}>
            {result?.sales.map((sale, index) => (
              <div className={s.box} key={sale.salesPerson}>
                <p style={{ textAlign: 'center' }} className={s.title}>
                  {sale.salesPerson}
                </p>
                <Row>
                  <Col xl="6" style={{ height: 200, paddingRight: 10 }}>
                    <p style={{ textAlign: 'center', fontSize: 12, fontWeight: 'bold' }}>
                      Remaining to Quota
                    </p>
                    {sale.remainingToQuotaChart === 'bar' ? (
                      <ResponsiveBar
                        data={[result.rtqBarSales[index]]}
                        keys={['Sold YTD', 'Forecast', 'Remaining to Quota with Forecast']}
                        margin={{ top: 20, right: 90, bottom: 50, left: 30 }}
                        padding={0.1}
                        innerPadding={20}
                        groupMode="grouped"
                        layout="vertical"
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors={getColors}
                        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                        axisTop={null}
                        axisRight={null}
                        enableGridY={false}
                        enableGridX={true}
                        axisBottom={null}
                        axisLeft={null}
                        labelSkipWidth={12}
                        labelSkipHeight={12}
                        markers={[
                          {
                            axis: 'y',
                            value: 0,
                            lineStyle: { stroke: 'red', strokeWidth: 1 }
                          }
                        ]}
                        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                        legends={[
                          {
                            dataFrom: 'keys',
                            anchor: 'bottom-left',
                            direction: 'row',
                            justify: false,
                            translateX: 0,
                            translateY: 50,
                            itemsSpacing: -30,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemDirection: 'left-to-right',
                            itemOpacity: 0.85,
                            symbolSize: 10,
                            effects: [
                              {
                                on: 'hover',
                                style: {
                                  itemOpacity: 1
                                }
                              }
                            ]
                          }
                        ]}
                        enableLabel={false}
                        tooltip={({ id, value }) => (
                          <span>
                            {id}:{' '}
                            <strong>
                              <NumericFormat
                                thousandSeparator
                                decimalScale={2}
                                value={value}
                                prefix={getCurrency().symbol}
                                displayType="text"
                              />
                            </strong>
                          </span>
                        )}
                      />
                    ) : (
                      <ResponsivePie
                        data={result.rtqPieSales[index]}
                        colors={{ datum: 'data.color' }}
                        margin={{ top: 20, right: 90, bottom: 50, left: 30 }}
                        padAngle={0.7}
                        cornerRadius={3}
                        fit={false}
                        activeOuterRadiusOffset={8}
                        borderWidth={1}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        enableArcLinkLabels={false}
                        arcLinkLabelsSkipAngle={10}
                        arcLinkLabelsThickness={2}
                        arcLinkLabelsColor={{ from: 'color' }}
                        enableArcLabels={false}
                        arcLabelsSkipAngle={10}
                        arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                        tooltip={({ datum }) => {
                          const { id, value } = datum
                          return (
                            <span>
                              {id}:{' '}
                              <strong>
                                <NumericFormat
                                  thousandSeparator
                                  decimalScale={2}
                                  value={value}
                                  prefix={getCurrency().symbol}
                                  displayType="text"
                                />
                              </strong>
                            </span>
                          )
                        }}
                        legends={[
                          {
                            anchor: 'bottom',
                            direction: 'row',
                            justify: false,
                            translateX: 30,
                            translateY: 50,
                            itemsSpacing: -30,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemTextColor: '#999',
                            itemDirection: 'left-to-right',
                            itemOpacity: 1,
                            symbolSize: 10,
                            symbolShape: 'square',
                            effects: [
                              {
                                on: 'hover',
                                style: {
                                  itemTextColor: '#000'
                                }
                              }
                            ]
                          }
                        ]}
                      />
                    )}
                  </Col>
                  <Col xl="6" style={{ height: 200, paddingRight: 10 }}>
                    <p style={{ textAlign: 'center', fontSize: 12, fontWeight: 'bold' }}>
                      SOLD YTD Revenue vs Profit
                    </p>
                    {sale.soldYtdChart === 'bar' ? (
                      <ResponsiveBar
                        data={[result.ytdBarSales[index]]}
                        keys={['Profit', 'Revenue - Profit']}
                        margin={{ top: 20, right: 90, bottom: 50, left: 30 }}
                        padding={0.1}
                        innerPadding={20}
                        groupMode="grouped"
                        layout="vertical"
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors={getColors}
                        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                        axisTop={null}
                        axisRight={null}
                        enableGridY={false}
                        enableGridX={true}
                        axisBottom={null}
                        axisLeft={null}
                        labelSkipWidth={12}
                        labelSkipHeight={12}
                        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                        markers={[
                          {
                            axis: 'y',
                            value: 0,
                            lineStyle: { stroke: 'red', strokeWidth: 1 }
                          }
                        ]}
                        legends={[
                          {
                            dataFrom: 'keys',
                            anchor: 'bottom-left',
                            direction: 'row',
                            justify: false,
                            translateX: 0,
                            translateY: 50,
                            itemsSpacing: -30,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemDirection: 'left-to-right',
                            itemOpacity: 0.85,
                            symbolSize: 10,
                            effects: [
                              {
                                on: 'hover',
                                style: {
                                  itemOpacity: 1
                                }
                              }
                            ]
                          }
                        ]}
                        enableLabel={false}
                        tooltip={({ id, value }) => (
                          <span>
                            {id}:{' '}
                            <strong>
                              <NumericFormat
                                thousandSeparator
                                decimalScale={2}
                                value={value}
                                prefix={getCurrency().symbol}
                                displayType="text"
                              />
                            </strong>
                          </span>
                        )}
                      />
                    ) : (
                      <ResponsivePie
                        data={result.ytdPieSales[index]}
                        colors={{ datum: 'data.color' }}
                        margin={{ top: 20, right: 90, bottom: 50, left: 30 }}
                        padAngle={0.7}
                        cornerRadius={3}
                        fit={false}
                        activeOuterRadiusOffset={8}
                        borderWidth={1}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        enableArcLinkLabels={false}
                        arcLinkLabelsSkipAngle={10}
                        arcLinkLabelsThickness={2}
                        arcLinkLabelsColor={{ from: 'color' }}
                        enableArcLabels={false}
                        arcLabelsSkipAngle={10}
                        arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                        tooltip={({ datum }) => {
                          const { id, value } = datum
                          return (
                            <span>
                              {id}:{' '}
                              <strong>
                                <NumericFormat
                                  thousandSeparator
                                  decimalScale={2}
                                  value={value}
                                  prefix={getCurrency().symbol}
                                  displayType="text"
                                />
                              </strong>
                            </span>
                          )
                        }}
                        legends={[
                          {
                            anchor: 'bottom-right',
                            direction: 'row',
                            justify: false,
                            translateX: 30,
                            translateY: 49,
                            itemsSpacing: 20,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemTextColor: '#999',
                            itemDirection: 'right-to-left',
                            itemOpacity: 1,
                            symbolSize: 10,
                            symbolShape: 'square',
                            effects: [
                              {
                                on: 'hover',
                                style: {
                                  itemTextColor: '#000'
                                }
                              }
                            ]
                          }
                        ]}
                      />
                    )}
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try different filters.</p>
      )}
    </>
  )
}

export default SalesAndForecast
